import { createSelector } from '@reduxjs/toolkit';

import { type RootState } from '../../../shared/types';

const vioReportTypes = ['SUMMARY', 'DETAIL', 'PERMISSIONS'] as const;

// Legacy export banner removed; no selector

export const selectExportIdsByReportType = (state: RootState) =>
  state.aag.report.exportIdsByReportType;

export const selectExportStatusByReportType = (state: RootState) =>
  state.aag.report.exportStatusByReportType ?? {};

export const selectExportProgress = (state: RootState) =>
  state.aag.report.exportProgress;

export const selectCompletedExportsCount = (state: RootState) =>
  state.aag.report.completedExportsCount;

export const selectLastStatusReportTypes = (state: RootState) =>
  state.aag.report.lastStatusReportTypes ?? [];

export const makeSelectExportIdForType = (reportType: string) =>
  createSelector([selectExportIdsByReportType], (ids) => ids[reportType]);

export const selectCompletedReportTypes = createSelector(
  [selectExportStatusByReportType, selectExportProgress],
  (statusByType, progressByType) => {
    const byStatus = Object.keys(statusByType).filter(
      (rt) =>
        statusByType[rt] === 'COMPLETED' &&
        (vioReportTypes as readonly string[]).includes(rt)
    );

    if (byStatus.length > 0) {
      return byStatus;
    }

    return Object.keys(progressByType).filter((rt) => {
      const progress = progressByType[rt];

      return (
        typeof progress === 'number' &&
        progress >= 100 &&
        (vioReportTypes as readonly string[]).includes(rt)
      );
    });
  }
);

export const selectVisibleCompletedItems = createSelector(
  [
    selectCompletedReportTypes,
    selectExportIdsByReportType,
    selectCompletedExportsCount,
    selectLastStatusReportTypes,
  ],
  (completedTypes, idsByType, completedCount, lastTypes) => {
    if (
      (!completedCount || completedCount <= 0) &&
      completedTypes.length === 0
    ) {
      return [] as { reportType: string; exportId?: string }[];
    }

    const items = completedTypes.map((reportType) => ({
      reportType,
      exportId: idsByType[reportType],
    }));

    const allowedReportTypes: string[] = Array.isArray(lastTypes)
      ? lastTypes.filter((rt): rt is string => typeof rt === 'string')
      : [];

    const gated =
      allowedReportTypes.length > 0
        ? items.filter((it) => allowedReportTypes.includes(it.reportType))
        : items;

    return gated as { reportType: string; exportId?: string }[];
  }
);
